import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { FormGroup } from 'src/components/ui';
import get from 'lodash/get';
import { getObject, mapObject, setObject } from '../../util/mapObject';
import { useJnx } from '../../util/jnx';
import Form from './Form';
import parseFormDefinition from '../../util/parseFormDefinition';
import getPathFromId from '../../util/getPathFromId';
import useResourceLoader from '../../util/useResourceLoader';
import FormApi from '../../api/FormApi';
import Loader from '../Loader';
import UWEFormContext from '../../context/UWEFormContext';
import { useSideChannelSubscription } from '../../util/useSideChannel';

function SubformField({
  registry,
  name,
  schema,
  onChange: propOnChange,
  disabled,
  fieldPathId: { $id, path: _path },
  formData: propFormData,
  readonly,
}) {
  const path = useMemo(() => getPathFromId($id), [$id]);

  const UweFormContext = useContext(UWEFormContext);
  const { addSubformToContext = () => {}, openTabs = {}, subForms = [], resolvedFormVersions } = UweFormContext ?? {};
  const { templates, widgets, fields, definitions, formContext: propFormContext } = registry;
  const { ArrayFieldTemplate, FieldTemplate, ObjectFieldTemplate } = templates ?? {};

  const { title, schemaExpr, schema: schemaPropDef, ['ui:subformName']: subformName } = schema;

  // The master form's resolve call (CurrentUWEEntityForm.jsx) already walked its schema for every
  // SubformField reference and resolved each one, so this subform's entry should already be in
  // the map — consulting it here means this subform honors the same Draft/Scheduled/Active/
  // Pinned resolution the master page does, instead of always fetching whatever's currently live.
  // Falling back to a plain by-name fetch only covers a reference the map genuinely doesn't have
  // an entry for (e.g. added to the schema after the map was computed) — not the normal path.
  const resolvedSubform = resolvedFormVersions?.[subformName];

  const [loadedForm, loadingForm = true, errorLoadingForm] = useResourceLoader(async () => {
    if (!subformName) return null;
    const form = resolvedSubform
      ? await FormApi.getVersion({ formId: resolvedSubform.formId, version: resolvedSubform.version })
      : await FormApi.getByName({ name: subformName });
    addSubformToContext({ ...form, 'map:field': schema['map:field'], path });
    return form;
  }, [subformName, resolvedSubform?.formId, resolvedSubform?.version]);

  const schemaJnx = useJnx(schemaExpr);

  const schemaDef = useMemo(() => {
    const _schemaDef =
      schemaJnx &&
      schemaJnx.eval(propFormData, '', {
        schema,
      });

    return _schemaDef || schemaPropDef;
  }, [propFormData, schemaJnx, schemaPropDef, schema]);

  const formDef = useMemo(() => {
    if (loadedForm) {
      return parseFormDefinition(loadedForm);
    }

    return parseFormDefinition({
      rootSchema: {
        ...schemaDef,
        definitions: {
          ...definitions,
          ...schemaDef?.definitions,
        },
      },
    });
  }, [schemaDef, definitions, loadedForm]);

  useEffect(() => {
    if (formDef && loadedForm?.name) {
      addSubformToContext({ ...loadedForm, 'map:field': schema['map:field'], path, formDef });
    }
  }, [formDef, loadedForm]);

  const [rootFormData] = useSideChannelSubscription(propFormContext.sideChannel, [0, 1]) || [{}];
  const sideChannel = useSubformSideChannel(propFormContext.sideChannel, path, formDef.invObjectMap, propFormContext);

  const formContext = {
    ...propFormContext,
    sideChannel,
    parentRootData: rootFormData,
    subformDataPrefix: path,
    subformDefinition: formDef,
  };

  const formData = useMemo(() => (formDef.invObjectMap ? mapObject(propFormData, formDef.invObjectMap, null, formContext) : propFormData), [
    propFormData,
    formDef,
    formContext,
  ]);

  const onChange = useCallback(
    ({ formData: _fd }) => {
      const { objectMapAsMap } = formDef;

      if (objectMapAsMap) {
        _fd = mapObject(_fd, objectMapAsMap, null, formContext);

        // Ocif-specific, dont want to lose data values not in form...
        // Only entity-shaped subforms carry a `data` envelope; element subforms (e.g. workpage
        // dashboard config) map to a flat object with no `data`, so guard against it.
        if (_fd?.data) {
          _fd.data.markedAsDone = get(propFormData, ['data', 'markedAsDone']) || false;
          _fd.data.uploadStatus = get(propFormData, ['data', 'uploadStatus']) || '';
        }
      }

      propOnChange(_fd, _path);
    },
    [propOnChange, formDef, formContext]
  );

  const isVisible = useMemo(() => {
    const tabGroup = schema?.['ui:tabGroup'];
    if (tabGroup) {
      const regex = new RegExp(`_${name}$`, '');
      const parentPath = $id.replace(regex, '');
      return openTabs[parentPath] === $id;
    }
    return true;
  }, [schema, openTabs, $id]);

  const errorsFromParent = useMemo(() => {
    if (formContext?.formErrors) {
      return get(formContext?.formErrors, path) || {};
    }
    return {};
  }, [path, formContext]);

  if (!isVisible) return null;

  if (subformName && loadingForm) {
    return <Loader />;
  }

  if (subformName && (subForms[subformName] == null || subForms[subformName].formDef == null)) {
    return <Loader />;
  }

  return (
    <FormGroup disabled>
      {title !== ' ' ? (
        <label className="control-label" htmlFor={$id}>
          {title}
        </label>
      ) : null}
      <Form
        tagName="div"
        disabled={disabled}
        idPrefix={$id}
        formData={formData}
        formContext={formContext}
        onChange={onChange}
        templates={{
          ArrayFieldTemplate,
          ObjectFieldTemplate,
          FieldTemplate,
          ErrorListTemplate: () => null,
        }}
        schema={formDef.schema}
        uiSchema={formDef.uiSchema}
        widgets={widgets}
        fields={fields}
        readonly={readonly}
        extraErrors={errorsFromParent}
      >
        <></>
      </Form>
    </FormGroup>
  );
}

function useSubformSideChannel(parentSidechannel, path, inverseMap, formContext) {
  const channel = useMemo(() => {
    const subscribers = [];
    let lastPublication = extractSubFormPublication(parentSidechannel?.getLastPublication());

    function extractSubFormPublication(publication) {
      if (!publication) return null;

      const rootFormData = publication[0];
      const formData = getObject(rootFormData, path);
      const subformData = mapObject(formData, inverseMap, null, formContext);
      const rootSubformData = {};
      setObject(rootSubformData, path, subformData);

      const subformPub = [rootSubformData, rootFormData];

      return subformPub;
    }

    function unsubscribe(fn) {
      const idx = subscribers.indexOf(fn);

      if (idx > -1) {
        subscribers.splice(idx, 1);
      }
    }

    function subscribe(fn) {
      subscribers.push(fn);
      fn(...(lastPublication || []));

      return () => unsubscribe(fn);
    }

    function publish(...data) {
      lastPublication = extractSubFormPublication(data);
      subscribers.forEach(fn => fn(...lastPublication));
    }

    return {
      getLastPublication() {
        return lastPublication;
      },
      subscribe,
      unsubscribe,
      publish,
    };
  }, [parentSidechannel, path, inverseMap, formContext]);

  useEffect(() => parentSidechannel.subscribe((...data) => channel.publish(...data)), [parentSidechannel, channel]);

  return channel;
}

export default SubformField;
